import { useEffect, useMemo, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useSearchParams } from 'react-router-dom';
import {
  api, externalDupConfirm, externalDupDone, uploadAllocChangeFile, type AllocChangeInput, type AllocChangePreview, type AllocChangeRow, type CarAlloc, type CarryKind,
} from '../lib/api';
import { ErrorBox, dash } from '../components/DataTable';
import Modal from '../components/Modal';
import { FitBox } from '../components/FitBox';
import { canScreen, useMe } from '../lib/me';
import ExternalAllocForm from '../components/ExternalAllocForm';
import { useAutoSearch } from '../lib/search';
import { SortTh, useSort } from '../lib/sort';
import { RefPicker } from './MasterForm';
import { AllocPicker } from './FinInst';
import { AllocFileChips, KindBadge, changeText, changeWhen, pokeAllocChanges, useAllocChangeMons } from '../components/AllocChangeAlerts';

/**
 * 운행편성 변경(2026-10-02 way) — 구글폼 「차량변경정보공유 프로세스」를 옮긴 화면.
 *
 *  · 한 판에서 본다: 신규·변경·종료(이 화면) + 기준정보에서 직접 고친 것 + 버스인에서 만든 편성까지 전부(`alloc_change`).
 *  · 등록하면 시스템이 처리한다: 옛 편성 종료일 · 새 편성 · 카드번호 · 남은 월정기·할부·재무추가 옮기기(한 번에).
 *  · 회계팀에는 팝업, 정산 화면에는 그 달 강조 띠가 뜬다(`components/AllocChangeAlerts`).
 */

const kstToday = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const thisMon = () => kstToday().slice(0, 7);
function addMonth(mon: string, d: number): string {
  const y = Number(mon.slice(0, 4));
  const m = Number(mon.slice(5, 7)) - 1 + d;
  const yy = y + Math.floor(m / 12);
  return `${yy}-${String(((m % 12) + 12) % 12 + 1).padStart(2, '0')}`;
}
const lastDay = (mon: string) => `${mon}-${String(new Date(Date.UTC(Number(mon.slice(0, 4)), Number(mon.slice(5, 7)), 0)).getUTCDate()).padStart(2, '0')}`;
const SOURCE_LABEL: Record<string, string> = { web: '편성 변경', form: '기준정보', legacy: '버스인', gform: '구글폼' };

/**
 * 맨 앞 체크(#245 정여진 — 「재무추가처럼 체크하면 초록으로 남게」). 재무추가 #142 와 같은 방식 —
 * 맞춰 본 줄 표시용이라 이 브라우저에만 기억한다(「확인」 칸 = 회계·영업 확인 기록과는 별개).
 */
const CHECK_KEY = 'businweb.allocChanges.checked';
function loadChecked(): Set<number> {
  try { return new Set((JSON.parse(localStorage.getItem(CHECK_KEY) ?? '[]') as unknown[]).map(Number)); } catch { return new Set(); }
}
function saveChecked(v: Set<number>) {
  try { localStorage.setItem(CHECK_KEY, JSON.stringify([...v].slice(-5000))); } catch { /* 저장 못 해도 화면은 그대로 */ }
}

export default function AllocChanges() {
  const [checked, setChecked] = useState<Set<number>>(loadChecked);
  const toggleCheck = (id: number) => setChecked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    saveChecked(next);
    return next;
  });
  const [params, setParams] = useSearchParams();
  const { me } = useMe();
  const canWrite = canScreen(me, 'master');
  /** 외부 편성 등록 — 기준정보 또는 배차 입력(서버 registerExternalAlloc 와 같은 게이트 · 10-22 김희정). */
  const canExt = canScreen(me, ['master', 'dispatch']);
  const [extOpen, setExtOpen] = useState(false);
  /** 최종 확인 — 관리자만(10-15 way). */
  const isAdmin = me?.role === 'admin';
  const [mon, setMon] = useState(() => (/^\d{4}-\d{2}$/.test(params.get('mon') ?? '') ? params.get('mon')! : thisMon()));
  const [allMon, setAllMon] = useState(false);
  /** 하루 반영 내역(11-04 way) — 값이 있으면 정산월 대신 그날 등록분 전부. */
  const [day, setDay] = useState('');
  const pendMons = useAllocChangeMons();
  const [kind, setKind] = useState('');
  const [belong, setBelong] = useState(''); // #268 내부/외부
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useAutoSearch(term, (v) => setQ(v));
  // 머리글 정렬(#403) — 300줄로 끊기는 목록이라 서버가 정렬한다(lib/sort 주석).
  const [sort, onSort] = useSort('allocChanges');
  const [rows, setRows] = useState<AllocChangeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [detail, setDetail] = useState<number | null>(() => Number(params.get('id')) || null);
  // 화면 탭(#168)으로 살아 있는 동안 알림·링크(`?mon=`·`?id=`)가 다시 오면 그 값으로 — 처음 열 때만 읽으면 무시된다.
  const urlMon = params.get('mon') ?? '';
  const urlId = Number(params.get('id')) || null;
  useEffect(() => { if (/^\d{4}-\d{2}$/.test(urlMon)) { setMon(urlMon); setAllMon(false); setDay(''); } }, [urlMon]);
  useEffect(() => { if (urlId) setDetail(urlId); }, [urlId]);
  const fromId = Number(params.get('from')) || null;
  const [form, setForm] = useState<{ fromId: number | null } | null>(null);
  // 기준정보 편성 창의 「편성 변경으로 처리」(?from=) — 권한을 받아 온 뒤 한 번 연다.
  useEffect(() => { if (fromId && canWrite) setForm((f) => f ?? { fromId }); }, [fromId, canWrite]);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.listAllocChanges({ mon: allMon || day ? undefined : mon, day: day || undefined, kind: kind || undefined, q: q || undefined, belong: belong || undefined, limit: 300, sort: sort?.key, dir: sort?.dir })
      .then((r) => { if (!cancelled) { setRows(r.rows); setTotal(r.total); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mon, allMon, day, kind, belong, q, nonce, sort]);

  function closeForm(saved?: string) {
    setForm(null);
    if (params.get('from')) { params.delete('from'); setParams(params, { replace: true }); }
    if (saved) { setNotice(saved); setNonce((n) => n + 1); pokeAllocChanges(); }
  }

  const unackedIds = rows.filter((r) => !r.acked && r.kind !== '수정').map((r) => Number(r.alloc_change_id));
  const unacked = unackedIds.length;
  // 10-12 way 「편성 일괄확인 버튼」 — 지금 목록에 보이는 내 미확인만 한 번에(필터·달을 좁혀 놓고 누르면 그만큼만).
  const [acking, setAcking] = useState(false);
  async function ackAll() {
    if (!unacked) return;
    setAcking(true);
    try {
      let n = 0;
      for (let i = 0; i < unackedIds.length; i += 200) n += (await api.ackAllocChanges({ ids: unackedIds.slice(i, i + 200) })).acked;
      setNotice(`${n}건을 확인했습니다.`); setNonce((x) => x + 1); pokeAllocChanges();
    } finally { setAcking(false); }
  }

  // 최종 확인 = 예외만(11-04 way · 서버 REVIEW_SQL) — 평범한 건은 「자동」으로 보이고 누를 것이 없다.
  const unfinalIds = rows.filter((r) => !r.final_at && r.kind !== '수정' && r.need_review).map((r) => Number(r.alloc_change_id));
  const [finaling, setFinaling] = useState(false);
  async function finalAll() {
    if (!unfinalIds.length || !window.confirm(`지금 목록에 보이는 ${unfinalIds.length}건을 최종 확인할까요?`)) return;
    setFinaling(true);
    try {
      let n = 0;
      for (let i = 0; i < unfinalIds.length; i += 200) n += (await api.finalAllocChanges({ ids: unfinalIds.slice(i, i + 200) })).done;
      setNotice(`${n}건을 최종 확인했습니다.`); setNonce((x) => x + 1);
    } finally { setFinaling(false); }
  }

  return (
    <div className="space-y-4">
      <div className="page-head">
        <h1 className="font-bold">운행편성 변경</h1>
        <p className="mt-1 text-sm text-ink-soft">
          편성의 신규·변경·종료를 여기서 등록하면 옛 편성 정리와 새 편성 등록이 한 번에 처리되고, 회계팀과 정산 담당에게 알려집니다.
          기준정보에서 직접 고친 것과 버스인에서 만든 편성도 함께 쌓입니다.
        </p>
      </div>

      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        {canWrite && <button className="btn-primary" onClick={() => setForm({ fromId: null })}>+ 편성 변경 등록</button>}
        {canExt && <button className="btn" onClick={() => setExtOpen(true)} title="관광사·개인 이름만 적으면 차주·기사·차량·편성이 양식대로 한 번에 만들어집니다">외부 편성 등록</button>}
        <div className="flex items-center gap-1">
          <button className="btn-ghost px-2" disabled={allMon || !!day} onClick={() => setMon((m) => addMonth(m, -1))} aria-label="이전 달">‹</button>
          <input type="month" className="field w-40" value={mon} disabled={allMon || !!day} onChange={(e) => e.target.value && setMon(e.target.value)} aria-label="정산월" />
          <button className="btn-ghost px-2" disabled={allMon || !!day} onClick={() => setMon((m) => addMonth(m, 1))} aria-label="다음 달">›</button>
        </div>
        <label className="flex items-center gap-1 text-sm text-ink-soft">
          <input type="checkbox" checked={allMon} disabled={!!day} onChange={(e) => setAllMon(e.target.checked)} /> 모든 달
        </label>
        {/* 하루 반영 내역(11-04 way) — 정산월과 무관하게 그날 등록한 것 전부(소급 등록이 다른 달에 숨지 않게) */}
        <div className={`flex items-center gap-1 rounded-lg px-1 ${day ? 'bg-brand-50 ring-1 ring-brand-300' : ''}`}>
          <button className={day ? 'btn-primary' : 'btn'} onClick={() => setDay((d) => (d ? '' : kstToday()))}
                  title="정산월과 상관없이 그날 등록한 편성 변경을 한 판에 봅니다 · 다시 누르면 정산월 보기로">
            {day ? '하루 반영 내역 닫기' : '오늘 반영 내역'}
          </button>
          {day && <input type="date" className="field w-40" value={day} max={kstToday()} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label="등록일" />}
        </div>
        <select className="field w-32" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="유형">
          <option value="">유형 전체</option>
          {['변경', '종료', '신규', '삭제', '수정'].map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select className="field w-32" value={belong} onChange={(e) => setBelong(e.target.value)} aria-label="내부/외부">
          <option value="">내부·외부 전체</option>
          <option value="내부">내부만</option>
          <option value="외부">외부만</option>
        </select>
        <input className="field w-60 flex-1 sm:flex-none" placeholder="차량번호·차주·기사·편성 NO" value={term} onChange={(e) => setTerm(e.target.value)} />
        <span className="ml-auto text-sm text-ink-soft">
          {total}건{unacked ? <> · <b className="text-amber-700">내가 확인 안 함 {unacked}</b></> : null}
        </span>
        {/* 늘 보인다 — 다 확인했으면 꺼진 채 「모두 확인함」(10-12 way 캡처: 다 확인한 상태라 버튼을 못 찾았다). */}
        <button className="btn" disabled={acking || unacked === 0} onClick={() => void ackAll()}
                title={unacked ? '지금 목록에 보이는 것 중 내가 아직 확인 안 한 줄을 한 번에 확인합니다' : '지금 목록에 보이는 줄은 모두 확인했습니다'}>
          {acking ? '확인 중…' : unacked ? `보이는 ${unacked}건 일괄 확인` : '일괄 확인 · 모두 확인함'}
        </button>
        {isAdmin && (
          <button className="btn-primary" disabled={finaling || unfinalIds.length === 0} onClick={() => void finalAll()}
                  title="최종 확인은 관리자만 합니다 — 지금 목록에 보이는 것 중 최종 확인 안 된 줄을 한 번에">
            {finaling ? '최종 확인 중…' : unfinalIds.length ? `보이는 ${unfinalIds.length}건 최종 확인` : '최종 확인 · 모두 끝남'}
          </button>
        )}
      </div>

      {/* 배지 건이 다른 달에 있으면 그 달로(11-04 way 캡처 — 배지 4 인데 11월 화면엔 확인할 게 없었다) */}
      {pendMons.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ink-soft">{isAdmin ? '최종 확인 남은 달' : '내가 확인 안 한 달'}:</span>
          {pendMons.map(([m, n]) => (
            <button key={m} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${!day && !allMon && m === mon ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
                    onClick={() => { setDay(''); setAllMon(false); setMon(m); }}>
              {Number(m.slice(5, 7))}월{m.slice(0, 4) !== thisMon().slice(0, 4) ? `(${m.slice(0, 4)})` : ''} {n}건
            </button>
          ))}
        </div>
      )}

      {error && <ErrorBox message={error} />}

      <FitBox deps={[rows.length, loading]} className="rounded-lg border border-line">
        <table className="min-w-full text-sm">
          <thead className="bg-canvas text-left text-xs text-ink-soft">
            <tr>
              <th className="w-8 px-2 py-2" title="맞춰 본 줄을 체크하면 초록으로 남습니다(이 PC에 기억)">체크</th>
              <SortTh className="px-3 py-2" sortKey="change_no" sort={sort} onSort={onSort}>NO</SortTh>
              <SortTh className="px-3 py-2" sortKey="created_at" sort={sort} onSort={onSort}>등록</SortTh>
              <SortTh className="px-3 py-2" sortKey="kind" sort={sort} onSort={onSort}>유형·사유</SortTh>
              <SortTh className="px-3 py-2" sortKey="apply_date" sort={sort} onSort={onSort}>적용</SortTh>
              <SortTh className="px-3 py-2" sortKey="old_car" sort={sort} onSort={onSort}>이전 편성</SortTh>
              <SortTh className="px-3 py-2" sortKey="new_car" sort={sort} onSort={onSort}>새 편성</SortTh>
              <SortTh className="px-3 py-2" sortKey="settle_mon" sort={sort} onSort={onSort}>정산월</SortTh>
              <SortTh className="px-3 py-2" sortKey="acked" sort={sort} onSort={onSort}>확인</SortTh>
              <SortTh className="px-3 py-2" sortKey="final_at" sort={sort} onSort={onSort}>최종 확인</SortTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading && rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-ink-faint">불러오는 중…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-ink-faint">이 조건의 편성 변경이 없습니다.</td></tr>}
            {rows.map((r) => (
              <tr key={r.alloc_change_id}
                  className={`cursor-pointer ${checked.has(Number(r.alloc_change_id)) ? 'bg-lime-200' : 'hover:bg-canvas'} ${r.need_review && !r.final_at ? 'border-l-4 border-l-amber-400' : ''}`}
                  title="두 번 누르면 상세"
                  onDoubleClick={() => { window.getSelection()?.removeAllRanges(); setDetail(r.alloc_change_id); }}>
                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="h-4 w-4 accent-emerald-600"
                         checked={checked.has(Number(r.alloc_change_id))}
                         onChange={() => toggleCheck(Number(r.alloc_change_id))} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.change_no}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-ink-soft">
                  {r.created_at}<br />{dash(r.created_name)} <span className="text-ink-faint">({SOURCE_LABEL[r.source] ?? r.source})</span>
                </td>
                <td className="px-3 py-2"><div className="flex items-center gap-1.5"><KindBadge kind={r.kind} />{r.is_ext && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">외부</span>}<span>{r.reason}</span>
                  {!!r.files?.length && <span className="text-xs text-ink-soft" title={r.files.map((f) => f.file_name).join(', ')}>📎{r.files.length}</span>}</div></td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{changeWhen(r)}</td>
                <td className="px-3 py-2">{r.old_snap ? <AllocCell s={r.old_snap} /> : <span className="text-ink-faint">—</span>}</td>
                {/* 종료엔 새 편성이 없다 — 버스인 이관분(legacy)은 끝난 뒤 모습을 new_snap 에 담아 같은 편성이 양쪽에 보였다(10-14 #236) */}
                <td className="px-3 py-2">{r.new_snap && r.kind !== '종료' ? <AllocCell s={r.new_snap} /> : <span className="text-ink-faint">—</span>}
                  {r.ext_form_bad && <span className="mt-1 inline-block rounded bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700" title="외부 편성은 기사·차량 = 개인_/관광사_ + 차주 이름 · 기준정보에서 이름을 고치면 사라집니다">외부 양식 위반</span>}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">{r.settle_mon}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">
                  {r.kind === '수정' ? <span className="text-ink-faint">—</span>
                    : r.source === 'gform' ? <span className="text-ink-soft">구글폼에서 확인</span>
                    : <>{r.my_acked !== false && r.acked ? <span className="text-emerald-700">나 확인</span>
                      : r.final_at ? <span className="text-ink-soft" title="최종 확인이 끝나 따로 확인하지 않아도 됩니다">최종 처리</span>
                      : <span className="font-semibold text-amber-700">미확인</span>}
                      <span className="text-ink-faint"> · {r.ack_cnt ?? 0}명</span></>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">
                  {r.kind === '수정' ? <span className="text-ink-faint">—</span>
                    : r.final_at ? <span className="font-semibold text-emerald-700" title={`${dash(r.final_name)} · ${r.final_at}`}>✓ 최종</span>
                    : r.need_review ? <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800" title="종료·삭제(종료일을 늘리기만 한 건 제외) · 지난 정산월로 소급 · 재배차할 노선 · 배정 없이 옮긴 통근 정산 줄 중 하나 — 관리자가 봐야 합니다">확인 필요</span>
                    : <span className="text-emerald-700" title="예외가 아니라 등록과 함께 자동 처리됐습니다(최종 확인 필요 없음)">자동</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </FitBox>

      {detail && (
        <Modal width="max-w-4xl">
          <AllocChangeDetail id={detail} onClose={() => { setDetail(null); setNonce((n) => n + 1); }} />
        </Modal>
      )}
      {extOpen && (
        <Modal width="max-w-4xl">
          <ExternalAllocForm onClose={() => setExtOpen(false)} onSaved={(msg) => { setExtOpen(false); setNotice(msg); setNonce((n) => n + 1); pokeAllocChanges(); }} />
        </Modal>
      )}
      {form && (
        <Modal width="max-w-3xl">
          <AllocChangeForm fromId={form.fromId} onClose={closeForm} />
        </Modal>
      )}
    </div>
  );
}

function AllocCell({ s }: { s: NonNullable<AllocChangeRow['old_snap']> }) {
  return (
    <div className="min-w-[12rem] leading-tight">
      <div className="font-medium">{dash(s.car_no)} <span className="text-xs text-ink-faint">NO {s.car_alloc_id}</span></div>
      <div className="text-xs text-ink-soft">
        {[s.owner_name ? `차주 ${s.owner_name}` : null, s.grp_name, s.driver_name ? `기사 ${s.driver_name}` : null].filter(Boolean).join(' · ') || '—'}
      </div>
    </div>
  );
}

// ─── 상세 — 확인한 사람 · 이 차량의 변경 이력 · 편성 저장 이력 ───────────────────────────────────

function AllocChangeDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { me } = useMe();
  const canPick = canScreen(me, ['master', 'settle']);
  const [d, setD] = useState<Awaited<ReturnType<typeof api.getAllocChange>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api.getAllocChange({ id }).then(setD).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id, nonce]);

  const canFix = canScreen(me, 'master');
  const [applyDraft, setApplyDraft] = useState('');
  async function fixApplyDate() {
    setBusy(true);
    setError(null);
    try { await api.setAllocChangeApplyDate({ id, date: applyDraft }); setApplyDraft(''); setNonce((n) => n + 1); pokeAllocChanges(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function pickSettle(pick: 'old' | 'new' | 'split') {
    setBusy(true);
    setError(null);
    try { await api.setAllocChangeSettle({ id, pick }); setNonce((n) => n + 1); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function addFiles(picked: File[]) {
    if (!picked.length) return;
    setBusy(true);
    setError(null);
    const failed: string[] = [];
    for (const f of picked) {
      try { await uploadAllocChangeFile(f, id); } catch (e) { failed.push(`${f.name} — ${e instanceof Error ? e.message : String(e)}`); }
    }
    if (failed.length) setError(`올리지 못한 파일: ${failed.join(' / ')}`);
    setBusy(false);
    setNonce((n) => n + 1);
  }

  async function removeFile(fileId: number) {
    if (!window.confirm('이 첨부를 내릴까요?')) return;
    setBusy(true);
    setError(null);
    try { await api.deleteAllocChangeFile({ fileId }); setNonce((n) => n + 1); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function setFinal(undo: boolean) {
    setBusy(true);
    try { await api.finalAllocChanges({ ids: [id], undo }); setNonce((n) => n + 1); } finally { setBusy(false); }
  }

  async function ack() {
    setBusy(true);
    try { await api.ackAllocChanges({ ids: [id] }); setNonce((n) => n + 1); pokeAllocChanges(); } finally { setBusy(false); }
  }

  const r = d?.row;
  const c = r?.carry;
  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-2">
        {r && <span className="font-mono text-sm text-ink-soft">NO {r.change_no}</span>}
        {r && <KindBadge kind={r.kind} />}
        <div className="text-lg font-bold">{r ? `${r.reason}` : '편성 변경'}</div>
        {r && <span className="text-sm text-ink-soft">{changeWhen(r)} · {r.settle_mon} 정산</span>}
        <div className="ml-auto flex gap-2">
          {r && r.kind !== '수정' && !r.acked && <button className="btn-primary" disabled={busy} onClick={() => void ack()}>확인했습니다</button>}
          {r && r.kind !== '수정' && me?.role === 'admin' && (r.final_at
            ? <button className="btn-ghost" disabled={busy} onClick={() => void setFinal(true)}>최종 확인 풀기</button>
            : <button className="btn-primary" disabled={busy} onClick={() => void setFinal(false)}>최종 확인</button>)}
          <button className="btn-ghost" onClick={onClose}>닫기</button>
        </div>
      </div>
      {error && <ErrorBox message={error} />}
      {!d && !error && <p className="text-sm text-ink-faint">불러오는 중…</p>}
      {r && (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <SnapCard title="이전 편성" s={r.old_snap} />
            <SnapCard title="새 편성" s={r.kind === '종료' ? null : r.new_snap} />
          </div>
          {(r.kind === '신규' || r.kind === '종료') && canFix && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas p-3 text-sm">
              <span className="font-semibold">{r.kind === '종료' ? '종료일' : '적용일자'}</span>
              <input type="date" className="field" value={applyDraft || r.apply_date} onChange={(e) => setApplyDraft(e.target.value)} />
              <button type="button" className="btn-ghost" disabled={busy || !applyDraft || applyDraft === r.apply_date} onClick={() => void fixApplyDate()}>{r.kind === '종료' ? '종료일 고치기' : '적용일자 고치기'}</button>
              <span className="text-xs text-ink-soft">
                {r.kind === '종료'
                  ? '고치면 그 편성의 종료일과 정산월이 같이 바뀝니다. 앞당길 때 새 종료일 뒤에 배차·정산이 있으면 막힙니다.'
                  : <>{r.source === 'legacy' ? '버스인에서 만든 편성은 시작일이 넘어오지 않아 버스인 등록일로 들어와 있습니다. ' : ''}고치면 정산월과 편성 시작일도 같이 맞춰집니다.</>}
              </span>
            </div>
          )}
          {r.kind === '변경' && (r.settle_alloc_id || d!.settle.pickable) && (
            <div className="rounded-lg border border-line bg-canvas p-3 text-sm">
              <span className="font-semibold">{r.settle_mon} 정산 기준 편성</span>{' '}
              {r.settle_alloc_id
                ? <>{r.settle_alloc_id === r.old_alloc_id ? '옛 편성' : '새 편성'}(NO {r.settle_alloc_id}) — 이 달 명세를 한 장으로 묶습니다</>
                : <span className="text-ink-soft">실제 날짜대로 나눔(명세 두 장)</span>}
              {canPick && d!.settle.pickable && !d!.settle.monthClosed && (
                <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                  {([['old', '옛 편성으로'], ['new', '새 편성으로'], ['split', '나눠 두기']] as const).map(([k, label]) => {
                    const on = k === 'split' ? !r.settle_alloc_id : r.settle_alloc_id === (k === 'old' ? r.old_alloc_id : r.new_alloc_id);
                    return <button key={k} type="button" disabled={busy || on} onClick={() => void pickSettle(k)}
                                   className={`rounded border px-2 py-0.5 text-xs ${on ? 'border-brand-600 bg-brand-50 font-semibold text-brand-700' : 'border-line text-ink-soft'}`}>{label}</button>;
                  })}
                </span>
              )}
              {d!.settle.monthClosed && <span className="ml-2 text-xs text-ink-faint">(마감된 달 — 바꿀 수 없음)</span>}
              <p className="mt-1 text-xs text-ink-soft">운행·배차는 실제 날짜 그대로이고, 정산현황은 바로 · 이미 만든 명세는 다시 만들어야 바뀝니다.</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold">첨부</span>
            {r.files?.length
              ? <AllocFileChips files={r.files} onRemove={canFix ? (fid) => void removeFile(fid) : undefined} />
              : <span className="text-ink-faint">없음</span>}
            {canFix && (
              <label className={`btn-ghost cursor-pointer text-xs ${busy ? 'pointer-events-none opacity-50' : ''}`}>
                + 파일 추가
                <input type="file" multiple className="hidden" onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  void addFiles(picked);
                }} />
              </label>
            )}
          </div>
          {(c || r.comment) && (
            <div className="rounded-lg border border-line bg-canvas p-3 text-sm">
              {c && (
                <div>
                  새 편성으로 옮긴 것({c.from ?? ''}~): 월정기 {c.moDefs ?? 0}개{c.moRows ? `(달 줄 ${c.moRows})` : ''} · 할부 {c.inst ?? 0}회 · 재무추가 {c.daily ?? 0}건
                  {c.cardsMoved ? ' · 주유·하이패스 카드 넘김' : ''}
                  {c.denied?.length ? <span className="text-amber-700"> · {c.denied.join('·')}은(는) 입력 권한이 없어 옮기지 않음</span> : null}
                </div>
              )}
              {r.comment && <div className="mt-1 text-ink-soft">비고: {r.comment}</div>}
            </div>
          )}
          <div className="text-sm">
            <span className="font-semibold">확인한 사람</span>{' '}
            {d!.acks.length ? d!.acks.map((a) => `${a.name}(${a.acked_at.slice(5)})`).join(', ')
              : r.source === 'gform' ? <span className="text-ink-soft">구글폼 절차에서 확인됨(비고의 처리 내용)</span>
              : <span className="text-ink-faint">아직 없음</span>}
            <span className="ml-2 text-xs text-ink-faint">등록 {r.created_name} · {r.created_at}</span>
            <span className="ml-2 text-xs">{r.final_at
              ? <span className="font-semibold text-emerald-700">최종 확인 {dash(r.final_name)} · {r.final_at}</span>
              : r.need_review ? <span className="font-semibold text-amber-700">최종 확인 필요(관리자) — 종료·삭제·소급·재배차·통근 줄 확인 중 하나</span>
              : <span className="text-emerald-700">자동 처리(예외 아님 · 최종 확인 필요 없음)</span>}</span>
          </div>

          <div>
            <div className="mb-1 text-sm font-semibold">이 차량의 편성 변경 이력</div>
            <ul className="max-h-56 divide-y divide-line overflow-y-auto rounded-lg border border-line text-sm">
              {d!.carHistory.map((h) => (
                <li key={h.alloc_change_id} className={`flex flex-wrap items-center gap-2 px-3 py-1.5 ${h.alloc_change_id === id ? 'bg-brand-50' : ''}`}>
                  <span className="w-20 shrink-0 font-mono text-xs text-ink-soft">{h.change_no}</span>
                  <span className="w-28 shrink-0 text-xs text-ink-faint">{h.created_at}</span>
                  <KindBadge kind={h.kind} />
                  <span className="text-xs">{h.reason} · {changeWhen(h)}</span>
                  <span className="min-w-0 flex-1 truncate" title={changeText(h)}>{changeText(h)}</span>
                  <span className="text-xs text-ink-faint">{h.created_name}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-1 text-sm font-semibold">편성 저장 이력 <span className="font-normal text-ink-faint">(저장할 때마다 남은 그때 값)</span></div>
            <div className="max-h-56 overflow-auto rounded-lg border border-line">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-canvas text-left text-ink-soft">
                  <tr>{['저장', 'NO', '차량', '차주', '소속', '기사', '기간', '정산', '저장한 사람'].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {d!.allocLog.map((l, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap px-2 py-1">{dash(l.created_at)}</td>
                      <td className="px-2 py-1">{dash(l.car_alloc_id)}</td>
                      <td className="px-2 py-1">{dash(l.car_no)}</td>
                      <td className="px-2 py-1">{dash(l.owner_name)}</td>
                      <td className="px-2 py-1">{dash(l.grp_name)}</td>
                      <td className="px-2 py-1">{dash(l.driver_name)}</td>
                      <td className="whitespace-nowrap px-2 py-1">{dash(l.startdt)} ~ {l.enddt ? String(l.enddt) : '무기한'}</td>
                      <td className="px-2 py-1">{l.is_use === false ? '종료' : '진행'}</td>
                      <td className="px-2 py-1">{dash(l.created_name)}</td>
                    </tr>
                  ))}
                  {d!.allocLog.length === 0 && <tr><td colSpan={10} className="px-2 py-3 text-center text-ink-faint">저장 이력이 없습니다(버스인에서 가져온 편성).</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SnapCard({ title, s }: { title: string; s: AllocChangeRow['old_snap'] }) {
  return (
    <div className="rounded-lg border border-line p-3 text-sm">
      <div className="mb-1 text-xs font-semibold text-ink-soft">{title}{s ? ` · NO ${s.car_alloc_id}` : ''}</div>
      {s ? (
        <dl className="grid grid-cols-[4rem_1fr] gap-y-0.5">
          <dt className="text-ink-faint">차량</dt><dd>{dash(s.car_no)}</dd>
          <dt className="text-ink-faint">차주</dt><dd>{dash(s.owner_name)}</dd>
          <dt className="text-ink-faint">소속</dt><dd>{dash(s.grp_name)}</dd>
          <dt className="text-ink-faint">기사</dt><dd>{dash(s.driver_name)}</dd>
          <dt className="text-ink-faint">기간</dt><dd>{dash(s.startdt)} ~ {s.enddt ? s.enddt : '무기한'}</dd>
        </dl>
      ) : <p className="text-ink-faint">—</p>}
    </div>
  );
}

// ─── 등록 — 변경 · 종료 · 신규 ──────────────────────────────────────────────────────────────────

type FormKind = '변경' | '종료' | '신규';
const REASONS: Record<FormKind, string[]> = {
  변경: ['기사 변경', '예비기사 변경', '차주 변경(양도양수)', '소속(법인) 변경', '차량 변경', '기타'],
  종료: ['매각', '퇴사', '폐차', '정보정리', '기타'],
  신규: ['신규 입사', '차량 신규', '예비기사 등록', '기타'],
};
const KIND_HELP: Record<FormKind, string> = {
  변경: '차량·차주·소속·기사 중 하나가 바뀝니다. 새 편성 시작일 전날로 옛 편성이 끝납니다.',
  종료: '매각·퇴사처럼 편성을 끝내기만 합니다.',
  신규: '새 차량·새 기사처럼 이어받을 편성 없이 새로 만듭니다.',
};
const CARRY_LABEL: Record<CarryKind, string> = { mo: '월정기', inst: '할부금', daily: '재무추가' };

interface Next { carId: number | null; ownerId: number | null; grpId: number | null; driverId: number | null }
interface Labels { car?: string; owner?: string; grp?: string; driver?: string }

function AllocChangeForm({ fromId, onClose }: { fromId: number | null; onClose: (saved?: string) => void }) {
  const [kind, setKind] = useState<FormKind>('변경');
  const [old, setOld] = useState<CarAlloc | null>(null);
  const [next, setNext] = useState<Next>({ carId: null, ownerId: null, grpId: null, driverId: null });
  const [labels, setLabels] = useState<Labels>({});
  const [date, setDate] = useState(() => `${addMonth(thisMon(), 1)}-01`);
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [comment, setComment] = useState('');
  /** 비고 첨부(#189) — 등록 누를 때 먼저 올리고 그 번호를 등록에 같이 보낸다. */
  const [files, setFiles] = useState<File[]>([]);
  const [moveCards, setMoveCards] = useState(true);
  const [settlePick, setSettlePick] = useState<'old' | 'new'>('old');
  const [carry, setCarry] = useState<Record<CarryKind, boolean>>({ mo: true, inst: true, daily: true });
  const [preview, setPreview] = useState<AllocChangePreview | null>(null);
  const [pvError, setPvError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 기준정보 편성 창에서 넘어온 경우(?from=) — 그 편성을 바로 고른다.
  useEffect(() => {
    if (!fromId) return;
    api.listCarAllocs({ q: String(fromId), limit: 20, scope: 'all' })
      .then((r) => { const hit = r.rows.find((x) => x.car_alloc_id === fromId); if (hit) pickOld(hit); })
      .catch(() => { /* 고르기 칸에서 다시 찾으면 된다 */ });
  }, [fromId]); // eslint-disable-line react-hooks/exhaustive-deps

  function pickOld(r: CarAlloc) {
    setOld(r);
    setNext({ carId: r.car_id, ownerId: r.owner_id, grpId: r.grp_id, driverId: r.driver_id });
    setLabels({ car: r.car_no ?? undefined, owner: r.owner_name ?? undefined, grp: r.grp_name ?? undefined, driver: r.driver_name ?? undefined });
  }

  function switchKind(k: FormKind) {
    setKind(k);
    setReason('');
    setReasonTouched(false);
    setPreview(null);
    if (k === '종료') setDate(lastDay(thisMon()));
    else if (k === '신규') { setDate(kstToday()); setNext({ carId: null, ownerId: null, grpId: null, driverId: null }); setLabels({}); }
    else setDate(`${addMonth(thisMon(), 1)}-01`);
    if (k !== '신규' && old) pickOld(old);
  }

  // 변경 사유 — 무엇이 바뀌었나로 미리 채운다(고르면 그대로 둔다).
  const changed = useMemo(() => {
    if (!old || kind !== '변경') return [] as string[];
    const out: string[] = [];
    if (next.carId !== old.car_id) out.push('차량');
    if (next.ownerId !== old.owner_id) out.push('차주');
    if (next.grpId !== old.grp_id) out.push('소속');
    if (next.driverId !== old.driver_id) out.push('기사');
    return out;
  }, [old, next, kind]);
  useEffect(() => {
    if (reasonTouched || kind !== '변경') return;
    const r = changed.includes('차량') ? '차량 변경' : changed.includes('차주') ? '차주 변경(양도양수)'
      : changed.includes('소속') ? '소속(법인) 변경' : changed.includes('기사') ? '기사 변경' : '';
    setReason(r);
  }, [changed, reasonTouched, kind]);

  // 옛 편성이 끝나는 날 — 변경은 새 편성 시작 전날.
  const endDate = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
    if (kind === '종료') return date;
    if (kind !== '변경') return '';
    const t = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))) - 86_400_000;
    return new Date(t).toISOString().slice(0, 10);
  }, [date, kind]);
  // 새 편성이 1일에 시작하면(옛 편성이 말일에 끝나면) 한 달을 두 편성이 나눠 타지 않는다.
  const splitMonth = kind === '변경' && !!endDate && date.slice(8, 10) !== '01';

  useEffect(() => {
    setPreview(null);
    setPvError(null);
    if (!old || !endDate || (kind !== '변경' && kind !== '종료')) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api.previewAllocChange({ oldAllocId: old.car_alloc_id, endDate, kind })
        .then((p) => { if (!cancelled) setPreview(p); })
        .catch((e) => { if (!cancelled) setPvError(e instanceof Error ? e.message : String(e)); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [old, endDate, kind]);

  // 그 달 정산 기준 편성(10-07 way) — 받는 사람이 그대로인 월 중간 변경만 한 편성으로 묶을 수 있다(서버 settlePickable 과 같은 조건).
  const directDriver = String(preview?.alloc?.driver_kind ?? '').startsWith('직영');
  const settleMon = endDate.slice(0, 7);
  const settleNote: { pickable: boolean; why: string } | null = (() => {
    if (kind !== '변경' || !splitMonth || !old || !next.carId) return null;
    if (next.driverId !== old.driver_id) return { pickable: false, why: `기사가 바뀌어 ${settleMon} 명세는 기사마다 자기 운행분으로 나뉩니다.` };
    if (!directDriver && next.ownerId !== old.owner_id) {
      return { pickable: false, why: `지급 받는 차주가 바뀌어 ${settleMon} 정산은 옛 편성·새 편성으로 나눠 둡니다(합치면 한 차주 몫이 다른 차주 명세로 갑니다).` };
    }
    if (next.carId === old.car_id && next.ownerId === old.owner_id) return null;
    return { pickable: !!old.driver_id, why: '' };
  })();

  const blocked = !!preview && (preview.blockedSchCount > 0 || preview.blockedSettled > 0 || preview.monthClosed);
  const leftRows: { k: CarryKind; n: number; text: string }[] = preview ? [
    { k: 'mo' as CarryKind, n: preview.left.moDefs + preview.left.moRows, text: `월정기 정기항목 ${preview.left.moDefs}개${preview.left.moRows ? `(이미 등록된 달 ${preview.left.moRows}줄)` : ''}` },
    { k: 'inst' as CarryKind, n: preview.left.inst, text: `할부 남은 회차 ${preview.left.inst}회` },
    { k: 'daily' as CarryKind, n: preview.left.daily, text: `재무추가 ${preview.left.daily}건` },
  ].filter((x) => x.n > 0) : [];
  const hasCards = !!preview && !!(preview.cards.fuel || preview.cards.hipass);

  async function submit() {
    setError(null);
    if (kind !== '신규' && !old) { setError('바꿀 편성을 고르세요.'); return; }
    if (!reason) { setError('사유를 고르세요.'); return; }
    if (kind === '변경' && changed.length === 0) { setError('차량·차주·소속·기사 중 바뀐 것이 없습니다. 끝내기만 하려면 「편성 종료」를 고르세요.'); return; }
    const input: AllocChangeInput = {
      kind, reason, comment: comment.trim() || undefined, date,
      oldAllocId: old?.car_alloc_id,
      next: kind === '종료' ? undefined : next,
      moveCards: kind === '변경' ? moveCards : undefined,
      carry: kind === '변경' ? (Object.keys(carry) as CarryKind[]).filter((k) => carry[k] && leftRows.some((x) => x.k === k)) : undefined,
      settlePick: settleNote?.pickable ? settlePick : undefined,
    };
    setBusy(true);
    try {
      // 외부 → 내부 이전(10-07 way) — 새 편성이 내부 소속이면 같은 연락처의 외부 편성이 끝난다. 먼저 알리고 확인을 받는다.
      if (kind !== '종료' && next.driverId && next.grpId) {
        const pv = await api.previewExternalDups({ driverId: next.driverId, grpId: next.grpId, startdt: date });
        if (pv.rows.length && !window.confirm(externalDupConfirm(pv))) return;
      }
      // 첨부(#189) — 먼저 올려(대기) 번호를 받고, 등록이 같은 트랜잭션에서 그 기록에 붙인다.
      const fileIds: number[] = [];
      for (const f of files) {
        try { fileIds.push((await uploadAllocChangeFile(f)).fileId); }
        catch (e) { throw new Error(`첨부 「${f.name}」을 올리지 못했습니다 — ${e instanceof Error ? e.message : String(e)}`); }
      }
      const r = await api.applyAllocChange({ ...input, fileIds: fileIds.length ? fileIds : undefined });
      const ext = r.endedExternal?.length ? ` ${externalDupDone(r.endedExternal)}.` : '';
      const s = r.sch;
      const schMsg = s && (s.trimmed || s.moved)
        ? ` ${[s.trimmed ? `배정 ${s.trimmed}건을 ${r.endDate}까지로 잘랐습니다.` : '', s.moved ? `종료일 뒤 배정 ${s.moved}건을 새 편성으로 옮겼습니다.` : '',
          s.created ? `새 편성이 ${s.created}건을 이어받았습니다.` : ''].filter(Boolean).join(' ')}`
          + (s.skipped.length ? ` 이어받기를 건너뛴 ${s.skipped.length}건(노선 ${s.skipped.map((x) => x.disptId ?? '-').join(', ')} — ${s.skipped[0]?.reason ?? ''})은 접수·배차 화면에서 배정하세요.` : '')
        : '';
      const forced = r.commuteForced?.length
        ? ` 이어받은 배정 없이 새 편성으로 옮긴 통근 정산 줄 ${r.commuteForced.length}건(${r.commuteForced.slice(0, 3).map((k) => `${k.driveMon} 노선 ${k.disptId ?? '-'}`).join(', ')}${r.commuteForced.length > 3 ? ' …' : ''})은 마감 전에 「노선·기사 편집」에서 확인하세요.`
        : '';
      const moved = r.carry ? [r.carry.moDefs ? `월정기 ${r.carry.moDefs}개` : '', r.carry.inst ? `할부 ${r.carry.inst}회` : '',
        r.carry.daily ? `재무추가 ${r.carry.daily}건` : ''].filter(Boolean).join(' · ') : '';
      onClose(kind === '변경'
        ? `${r.endDate}까지 옛 편성(NO ${r.oldAllocId})을 끝내고 새 편성 NO ${r.newAllocId} 을 만들었습니다.${r.settleAllocId ? ` ${r.endDate.slice(0, 7)} 정산은 ${r.settleAllocId === r.oldAllocId ? '옛' : '새'} 편성(NO ${r.settleAllocId}) 한 장으로 묶습니다.` : ''}${moved ? ` 옮긴 것: ${moved}.` : ''}${r.denied.length ? ` ${r.denied.join('·')}은(는) 입력 권한이 없어 옮기지 않았습니다.` : ''}${schMsg}${forced}${ext}`
        : kind === '종료' ? `편성 NO ${r.oldAllocId} 을 ${r.endDate}까지로 끝냈습니다.${schMsg}`
          : `새 편성 NO ${r.newAllocId} 을 만들었습니다.${ext}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const pick = (k: keyof Next, label: keyof Labels) => (id: number | null) => {
    setNext((n) => ({ ...n, [k]: id }));
    setLabels((l) => ({ ...l, [label]: undefined }));
  };

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-2">
        <div className="text-lg font-bold">편성 변경 등록</div>
        <button className="btn-ghost ml-auto" onClick={() => onClose()}>닫기</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['변경', '종료', '신규'] as FormKind[]).map((k) => (
          <button key={k} type="button" onClick={() => switchKind(k)}
                  className={`rounded-lg border px-3 py-1.5 text-sm ${kind === k ? 'border-brand-600 bg-brand-50 font-semibold text-brand-700' : 'border-line text-ink-soft'}`}>
            {k === '변경' ? '편성 변경' : k === '종료' ? '편성 종료' : '신규 편성'}
          </button>
        ))}
      </div>
      <p className="text-sm text-ink-soft">{KIND_HELP[kind]}</p>

      {kind !== '신규' && (
        <div className="space-y-1">
          <div className="text-sm font-semibold">지금 편성</div>
          <AllocPicker includeFuture value={old?.car_alloc_id ?? null} onPick={() => { /* onPickRow 가 채운다 */ }} onPickRow={(r) => pickOld(r as CarAlloc)}
                       label={old ? `${dash(old.car_no)} · ${dash(old.owner_name)} · ${dash(old.driver_name)}` : null} />
          {old && (
            <p className="text-xs text-ink-soft">
              NO {old.car_alloc_id} · {dash(old.grp_name)} · {dash(old.startdt)} ~ {old.enddt ? old.enddt : '무기한'}
            </p>
          )}
        </div>
      )}

      {kind !== '종료' && (kind === '신규' || old) && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">{kind === '변경' ? '새 편성 — 바뀌는 것만 고르세요' : '새 편성'}</div>
          <div className="grid gap-2 md:grid-cols-2">
            {([
              ['carId', 'car', 'car', '차량'], ['driverId', 'driver', 'driver', '기사'],
              ['ownerId', 'owner', 'owner', '차주'], ['grpId', 'grp', 'grp', '소속'],
            ] as const).map(([k, ref, lab, title]) => (
              <div key={k} className={kind === '변경' && old && next[k] !== old[k === 'carId' ? 'car_id' : k === 'driverId' ? 'driver_id' : k === 'ownerId' ? 'owner_id' : 'grp_id'] ? 'rounded-lg bg-amber-50 p-1' : 'p-1'}>
                <div className="mb-0.5 text-xs text-ink-soft">{title}</div>
                <RefPicker key={`${k}-${old?.car_alloc_id ?? 'new'}`} kind={ref} value={next[k]} label={labels[lab]} onPick={pick(k, lab)} />
              </div>
            ))}
          </div>
          {kind === '변경' && changed.length > 0 && <p className="text-xs text-amber-700">바뀌는 것: {changed.join('·')}</p>}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-0.5 block text-xs text-ink-soft">{kind === '종료' ? '종료일' : '새 편성 시작일'}</span>
          <input type="date" className="field w-full" value={date} onChange={(e) => setDate(e.target.value)} />
          {kind === '변경' && endDate && <span className="mt-0.5 block text-xs text-ink-soft">옛 편성은 {endDate}까지 운행(정산 진행중 체크는 그대로) · {splitMonth ? (settleNote?.pickable ? `${endDate.slice(0, 7)} 정산은 아래에서 고른 편성 한 장으로` : `${endDate.slice(0, 7)} 정산에서 두 편성이 나뉩니다`) : `${endDate.slice(0, 7)} 정산까지 옛 편성 · ${date.slice(0, 7)}부터 새 편성`}</span>}
        </label>
        <label className="block text-sm">
          <span className="mb-0.5 block text-xs text-ink-soft">사유</span>
          <select className="field w-full" value={reason} onChange={(e) => { setReason(e.target.value); setReasonTouched(true); }}>
            <option value="">고르세요</option>
            {REASONS[kind].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>

      {settleNote && (
        <div className="rounded-lg border border-line bg-canvas p-3 text-sm">
          <div className="font-semibold">{settleMon} 정산 기준 편성</div>
          {settleNote.pickable ? (
            <>
              <div className="mt-1 flex flex-wrap gap-4">
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="settle-pick" checked={settlePick === 'old'} onChange={() => setSettlePick('old')} />
                  옛 편성({dash(old?.car_no)} · {dash(old?.owner_name)}) <span className="text-xs text-ink-faint">기본</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" name="settle-pick" checked={settlePick === 'new'} onChange={() => setSettlePick('new')} />
                  새 편성({date}부터)
                </label>
              </div>
              <p className="mt-1 text-xs text-ink-soft">같은 기사라 {settleMon} 명세를 고른 편성 한 장으로 묶습니다(금액은 그대로). 운행·배차는 실제 날짜대로입니다. 등록 뒤에도 상세에서 바꿀 수 있습니다.</p>
            </>
          ) : <p className="mt-1 text-xs text-ink-soft">{settleNote.why}</p>}
        </div>
      )}

      {pvError && <ErrorBox message={pvError} />}
      {preview && (
        <div className="space-y-2">
          {blocked && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {preview.blockedSchCount > 0 && (
                <>
                  <div className="font-semibold">{endDate} 뒤에 시작하는 배차가 {preview.blockedSchCount}건 있어 끝낼 수 없습니다 — 누가 운행할지 정해야 합니다.</div>
                  <p className="text-xs">이어받을 편성이 있으면 「편성 변경」으로 등록하세요(새 편성으로 자동으로 옮깁니다). 아니면 접수·배차 화면에서 다시 배정하거나 날짜를 바꾼 뒤 등록하세요.</p>
                  <ul className="mt-1 max-h-32 overflow-y-auto text-xs">
                    {preview.blockedSch.map((s) => (
                      <li key={s.dispt_car_sch_id}>접수 {dash(s.recv_id)} · 노선 {dash(s.dispt_id)} {dash(s.dispt_name)} · {dash(s.cust_name)} · {dash(s.start_time)}</li>
                    ))}
                  </ul>
                </>
              )}
              {preview.blockedSettled > 0 && (
                <div className="font-semibold">
                  {kind === '변경'
                    ? `${endDate.slice(0, 7)} 뒤 마감된 달의 통근 정산 줄이 ${preview.blockedSettled}건 있어 바꿀 수 없습니다.`
                    : `${endDate.slice(0, 7)} 뒤 정산 이력이 ${preview.blockedSettled}건 있어 끝낼 수 없습니다.`}
                </div>
              )}
            </div>
          )}
          {!blocked && (preview.moveSettled ?? 0) > 0 && (
            <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
              <div className="font-semibold">{endDate.slice(0, 7)} 뒤 통근 정산 줄 {preview.moveSettled}건을 새 편성으로 옮깁니다.</div>
              <p className="text-xs">운행수와 단가는 그대로 두고 차량·기사·차주만 새 편성으로 바뀝니다.</p>
              {(preview.forceSettled ?? 0) > 0 && (
                <>
                  <p className="mt-1 text-xs font-semibold text-amber-800">
                    그중 {preview.forceSettled}건은 옛 편성의 그 노선 배정이 {endDate} 전에 끝나 이어받을 배정이 없습니다 — 배정 없이 새 편성으로 옮깁니다.
                    맞지 않으면 마감 전에 정산 › 거래처별 정산 「노선·기사 편집」에서 정리하세요.
                  </p>
                  <ul className="mt-1 max-h-24 overflow-y-auto text-xs">
                    {(preview.forceSettledSample ?? []).map((k, i) => (
                      <li key={i}>{k.driveMon} · 노선 {dash(k.disptId)} {dash(k.disptName)}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          {(preview.trimSch > 0 || preview.moveSch > 0) && (
            <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
              <div className="font-semibold">
                {[preview.trimSch ? `${endDate}까지로 자를 배정 ${preview.trimSch}건` : '', preview.moveSch ? `새 편성으로 옮길 배정 ${preview.moveSch}건` : ''].filter(Boolean).join(' · ')}
              </div>
              <p className="text-xs">
                {kind === '변경'
                  ? `종료일에 걸친 배정(통근 장기 배정 등)은 ${endDate}까지로 자르고 같은 노선을 새 편성이 ${date}부터 이어받습니다. 종료일 뒤에만 있는 배정은 새 편성으로 옮깁니다. 같은 노선·시간에 이미 배정이 있으면 그 건만 건너뜁니다(등록 뒤 알려 드립니다).`
                  : `종료일에 걸친 배정(통근 장기 배정 등)은 ${endDate}까지로 자릅니다. 그 뒤 운행은 접수·배차 화면에서 새로 배정하세요.`}
              </p>
              <ul className="mt-1 max-h-32 overflow-y-auto text-xs">
                {preview.schSample.filter((s) => s.act !== 'block').map((s) => (
                  <li key={s.dispt_car_sch_id}>{s.act === 'trim' ? '자름' : '옮김'} · 접수 {dash(s.recv_id)} · 노선 {dash(s.dispt_id)} {dash(s.dispt_name)} · {dash(s.cust_name)} · {dash(s.start_time)} ~ {dash(s.end_time)}</li>
                ))}
              </ul>
            </div>
          )}
          {kind === '변경' && leftRows.length > 0 && (
            <div className="rounded-lg border border-line bg-canvas p-3 text-sm">
              <div className="font-semibold">{preview.carryFrom}부터 옛 편성에 남은 것 — 새 편성으로 옮깁니다</div>
              <p className="text-xs text-ink-soft">{splitMonth ? `${endDate.slice(0, 7)}(두 편성이 나눠 탄 달)은 그대로 두고 그다음 달부터 옮깁니다.` : `${endDate.slice(0, 7)}까지는 옛 편성에 그대로 두고 ${preview.carryFrom.slice(0, 7)}부터 옮깁니다.`} 마감된 달은 옮기지 않습니다.</p>
              {leftRows.map((x) => {
                const can = preview.canCarry.includes(x.k);
                return (
                  <label key={x.k} className="mt-1 flex items-center gap-2">
                    <input type="checkbox" disabled={!can} checked={can && carry[x.k]} onChange={(e) => setCarry((c) => ({ ...c, [x.k]: e.target.checked }))} />
                    {x.text}{!can && <span className="text-xs text-amber-700">({CARRY_LABEL[x.k]} 입력 권한이 없어 옮길 수 없습니다)</span>}
                  </label>
                );
              })}
            </div>
          )}
          {kind === '종료' && leftRows.length > 0 && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {preview.carryFrom}부터 이 편성에 {leftRows.map((x) => x.text).join(' · ')}이 남아 있습니다. 이어받을 편성이 있으면 「편성 변경」으로 등록하세요.
            </p>
          )}
          {kind === '변경' && hasCards && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={moveCards} onChange={(e) => setMoveCards(e.target.checked)} />
              주유·하이패스 카드({[preview.cards.fuel, preview.cards.hipass].filter(Boolean).join(' · ')})를 새 편성으로 넘기기
              <span className="text-xs text-ink-faint">(차량이 바뀌면 주유카드는 옛 차량에 남고, 그 차량에 새 편성이 서면 따라갑니다)</span>
            </label>
          )}
          {preview.monthClosed && <p className="text-sm font-semibold text-red-700">{preview.closedMon}은 정산이 마감된 달이라 편성을 바꿀 수 없습니다. 마감되지 않은 달의 날짜로 넣어 주세요.</p>}
        </div>
      )}

      <label className="block text-sm">
        <span className="mb-0.5 block text-xs text-ink-soft">비고 (회계팀 알림에 같이 나갑니다)</span>
        <textarea className="field w-full" rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="예) 1/1일자로 법인이동(서울→파주) / TS 신규교육 예약완료" />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="btn-ghost cursor-pointer">
          📎 파일 첨부
          <input type="file" multiple className="hidden" onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            setFiles((cur) => [...cur, ...picked].slice(0, 10));
          }} />
        </label>
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1 text-xs">
            <span className="max-w-[16rem] truncate">{f.name}</span>
            <button type="button" className="text-ink-faint hover:text-red-600" title="빼기" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>
          </span>
        ))}
        {files.length === 0 && <span className="text-xs text-ink-faint">사진·PDF·엑셀 등 (한 파일 20MB까지) — 알림에 같이 보입니다</span>}
      </div>

      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={() => onClose()}>취소</button>
        <button className="btn-primary" disabled={busy || blocked || (kind !== '신규' && !old)} onClick={() => void submit()}>
          {busy ? '처리 중…' : kind === '변경' ? '변경 등록' : kind === '종료' ? '종료 등록' : '신규 등록'}
        </button>
      </div>
    </div>
  );
}

